import { taxonomyAttributeToFacetKey } from 'src/adapters/pageURLToStore/shop/constants';
import {
  type ShopTaxonomyLevels,
  isTaxonomySkipSlot,
} from 'src/adapters/pageURLToStore/shop/shopTaxonomyPath';
import type { Facet, FacetOption } from 'src/redux/productsQuery/types/Facet';

const buildSyntheticTaxonomyFacet = (attribute: string, activeValues: string[]): Facet => ({
  alias: attribute,
  key: taxonomyAttributeToFacetKey(attribute),
  options: activeValues.map(
    (value): FacetOption => ({
      active: true,
      count: null,
      title: value,
      value,
    }),
  ),
  title: attribute,
  type: 'multi',
  valueType: 'text',
});

/**
 * Sets `active: true` on each taxonomy facet's options that match the corresponding shop path slot.
 * Slot values missing from a facet's options are appended (count `null`) so they survive the
 * listings-response merge in `getUpdatedOptionsForTextFacet`. Taxonomy facets that aren't present
 * in the listings-controls config are created on the fly so `adaptStoreToRequestBody` still emits
 * the corresponding text filter for the listings query. Non-taxonomy facets are left alone.
 */
export const applyShopTaxonomyToFacets = (
  facets: Facet[],
  taxonomyLevels: ShopTaxonomyLevels,
  taxonomyAttributes: string[],
): Facet[] => {
  const slotByKey = new Map<string, string[]>();
  taxonomyAttributes.forEach((attribute, index) => {
    const slot = taxonomyLevels[index];
    const values = slot && !isTaxonomySkipSlot(slot) ? slot : [];
    slotByKey.set(taxonomyAttributeToFacetKey(attribute), values);
  });

  const seenKeys = new Set<string>();
  const updated = facets.map((facet) => {
    const slot = slotByKey.get(facet.key);
    if (slot === undefined) {
      return facet;
    }
    seenKeys.add(facet.key);

    const activeValues = new Set(slot);
    const existingValues = new Set(facet.options.map((option) => option.value));
    const updatedOptions: FacetOption[] = facet.options.map((option) => ({
      ...option,
      active: activeValues.has(option.value),
    }));

    slot.forEach((value) => {
      if (!existingValues.has(value)) {
        updatedOptions.push({
          active: true,
          count: null,
          title: value,
          value,
        });
      }
    });

    return { ...facet, options: updatedOptions };
  });

  taxonomyAttributes.forEach((attribute) => {
    const key = taxonomyAttributeToFacetKey(attribute);
    if (seenKeys.has(key)) {
      return;
    }
    const slot = slotByKey.get(key) ?? [];
    if (slot.length === 0) {
      return;
    }
    updated.push(buildSyntheticTaxonomyFacet(attribute, slot));
  });

  return updated;
};
